import jwt_decode from 'jwt-decode';
import API_BASE_URL from './apiBaseUrl';
import { TOKEN_KEY } from '../utilities/constants';
import { loginPathFor, navigateTo } from './nextPath';

/**
 * Token Refresh Service - API Agnostic Design
 * Handles automatic JWT token refresh to keep users logged in as long as possible
 * 
 * Core Philosophy:
 * - Keep trying to refresh tokens until the API says "no more"
 * - No hardcoded time limits or refresh windows
 * - Adapts to any API refresh policy automatically
 * 
 * API Response Handling:
 * - 200: Token refreshed successfully → schedule next refresh
 * - 425: Token still valid → reschedule for later
 * - 401/403: Refresh no longer possible → logout user
 * - Other errors: Retry with backoff, then logout if persistent
 */

class TokenRefreshService {
	constructor() {
		this.refreshTimer = null;
		this.isRefreshing = false;
		this.failedQueue = [];
		this.maxRetries = 3;
		this.retryCount = 0;
		
		// Timing configuration
		this.rescheduleDelaySeconds = 1; // How long to wait after a 425 response before retrying
		
		// Configurable redirect behavior
		this.autoRedirectOnFailure = true; // Auto redirect to login on refresh failure
		this.redirectDelay = 1000; // Delay before redirect (ms)
		this.showNotification = true; // Show user notification before redirect
	}

	/**
	 * Initialize token refresh service
	 * Sets up automatic refresh timing based on token expiration
	 */
	initialize(token) {
		if (!token) return;

		try {
			const decoded = jwt_decode(token);
			this.scheduleRefresh(decoded);
		} catch (error) {
			console.error('Failed to decode token for refresh scheduling:', error);
		}
	}

	/**
	 * Schedule the next token refresh
	 * Refreshes only after token expiration (API requirement)
	 */
	scheduleRefresh(decodedToken) {
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer);
		}

		const now = Date.now() / 1000;
		const exp = decodedToken.exp;
		
		// Calculate time until expiration plus a small delay (100ms) to ensure token is fully expired
		const timeUntilExpiration = Math.max(0, exp - now);
		const refreshDelay = timeUntilExpiration + 0.1; // Add 100ms after expiration
		
		console.log('Token refresh scheduled:', {
			expiresAt: new Date(exp * 1000).toLocaleString(),
			refreshIn: refreshDelay,
			refreshAt: new Date((now + refreshDelay) * 1000).toLocaleString()
		});

		if (exp <= now) {
			// Token already expired, refresh immediately
			this.refreshToken();
		} else {
			// Schedule refresh for just after expiration
			this.refreshTimer = setTimeout(() => {
				this.refreshToken();
			}, refreshDelay * 1000);
		}
	}

	/**
	 * Attempt to refresh the access token
	 * API-agnostic: keeps trying until API says no more
	 */
	async refreshToken() {
		if (this.isRefreshing) {
			// If already refreshing, queue this request
			return new Promise((resolve, reject) => {
				this.failedQueue.push({ resolve, reject });
			});
		}

		const token = localStorage.getItem(TOKEN_KEY);
		if (!token) {
			console.log('No token found for refresh');
			return null;
		}

		let parsedToken;
		try {
			parsedToken = JSON.parse(token);
		} catch (error) {
			console.error('Failed to parse stored token:', error);
			return null;
		}

		const accessToken = parsedToken.access_token;
		if (!accessToken) {
			console.error('No access token found in stored token');
			return null;
		}

		this.isRefreshing = true;

		try {
			console.log('Attempting token refresh...');
			
			const response = await fetch(`${API_BASE_URL}/refresh`, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${accessToken}`,
					'Content-Type': 'application/json'
				}
			});

			// Handle specific response codes
			if (response.status === 425) {
				// Token is still valid, API says "not yet" - wait until expiration
				console.log('Token still valid (425), rescheduling for after expiration...');
				this.rescheduleRefresh(accessToken);
				this.processQueue(null, accessToken);
				return accessToken;
			}

			if (response.status === 401 || response.status === 403) {
				// API says refresh is no longer possible
				console.log(`Refresh no longer possible (${response.status}), session ended`);
				throw new Error(`Refresh denied by API: ${response.status} ${response.statusText}`);
			}

			if (!response.ok) {
				// Other HTTP errors - might be temporary
				throw new Error(`Refresh request failed: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			
			if (!data.access_token) {
				throw new Error('No access token in refresh response');
			}

			// Success! Update stored token
			const newTokenData = {
				...parsedToken, // Preserve existing data
				access_token: data.access_token,
				refreshed_at: new Date().toISOString(),
				refresh_count: (parsedToken.refresh_count || 0) + 1
			};

			localStorage.setItem(TOKEN_KEY, JSON.stringify(newTokenData));
			
			console.log('Token refreshed successfully', {
				refreshCount: newTokenData.refresh_count,
				newExpiration: this.getTokenExpiration(data.access_token)
			});
			
			// Schedule next refresh based on new token
			this.initialize(data.access_token);
			
			// Reset retry count on success
			this.retryCount = 0;
			
			// Process queued requests
			this.processQueue(null, data.access_token);
			
			// Dispatch success event
			this.dispatchEvent('tokenRefreshed', { newToken: data.access_token });
			
			return data.access_token;
			
		} catch (error) {
			console.error('Token refresh failed:', error);
			
			// Determine if this is a permanent failure (401/403) or temporary
			const isPermanentFailure = error.message.includes('401') || error.message.includes('403');
			
			if (isPermanentFailure) {
				// Don't retry permanent failures
				console.log('Permanent refresh failure, ending session');
				this.handleRefreshFailure(error.message);
				this.processQueue(error, null);
				return null;
			}
			
			// For temporary failures, implement retry logic
			this.retryCount++;
			
			if (this.retryCount < this.maxRetries) {
				console.log(`Retrying refresh (${this.retryCount}/${this.maxRetries}) after temporary failure...`);
				
				// Exponential backoff: 5s, 10s, 20s
				const delay = Math.pow(2, this.retryCount) * 2500;
				
				setTimeout(() => {
					this.isRefreshing = false;
					this.refreshToken();
				}, delay);
				
				return null;
			} else {
				// Max retries exceeded even for temporary failures
				console.log('Max retries exceeded, ending session');
				this.handleRefreshFailure(`Max retries exceeded: ${error.message}`);
				this.processQueue(error, null);
				return null;
			}
		} finally {
			this.isRefreshing = false;
		}
	}

	/**
	 * Reschedule refresh after a 425 response (token still valid)
	 * Since tokens can only be refreshed after expiration, wait until then plus configured delay
	 */
	rescheduleRefresh(currentToken) {
		try {
			const decoded = jwt_decode(currentToken);
			const now = Date.now() / 1000;
			const timeUntilExpiration = decoded.exp - now;
			
			// Wait until expiration plus the configured delay
			const delay = timeUntilExpiration + this.rescheduleDelaySeconds;
			
			console.log(`Rescheduling refresh for ${Math.round(delay)} seconds (${this.rescheduleDelaySeconds}s after expiration)`);
			
			this.refreshTimer = setTimeout(() => {
				this.refreshToken();
			}, delay * 1000);
			
		} catch (error) {
			console.error('Failed to reschedule refresh:', error);
			// Fallback: try again in 30 seconds
			setTimeout(() => this.refreshToken(), 30000);
		}
	}

	/**
	 * Handle complete refresh failure - clean up and redirect to login
	 */
	handleRefreshFailure(reason) {
		console.log('Token refresh completely failed, ending session. Reason:', reason);
		
		// Clean up tokens and timers
		this.cleanup();
		
		// Dispatch failure event for app integration (logging, analytics, etc.)
		this.dispatchEvent('tokenRefreshFailed', { reason });
		
		// Handle user redirect
		if (this.autoRedirectOnFailure) {
			this.handleUserRedirect(reason);
		}
	}

	/**
	 * Handle redirecting user to login screen
	 */
	handleUserRedirect(reason) {
		// Show user notification if enabled
		if (this.showNotification) {
			// Try to show a user-friendly notification
			this.showSessionExpiredNotification(reason);
		}

		// Login URL with the reason, and next so the user returns to this page (ASB-4053)
		const redirectUrl = loginPathFor(window.location, { reason: 'session_expired', detail: reason });
		
		// Redirect after delay to allow user to see notification
		setTimeout(() => {
			// A full page load (window.location.href) works across all scenarios
			navigateTo(redirectUrl);
		}, this.redirectDelay);
	}

	/**
	 * Show session expired notification to user
	 */
	showSessionExpiredNotification(reason) {
		const userMessage = this.getUserFriendlyMessage(reason);
		console.warn('Session expired:', userMessage);
	}

	/**
	 * Convert technical error to user-friendly message
	 */
	getUserFriendlyMessage(reason) {
		if (reason.includes('401') || reason.includes('403')) {
			return 'Your session has expired. You will be redirected to the login page.';
		}
		
		if (reason.includes('Max retries exceeded')) {
			return 'Connection issues prevented session renewal. You will be redirected to the login page.';
		}
		
		// Default message
		return 'Your session has ended. You will be redirected to the login page.';
	}

	/**
	 * Clean up timers and storage
	 */
	cleanup() {
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer);
			this.refreshTimer = null;
		}
		
		// Clear stored token
		localStorage.removeItem(TOKEN_KEY);
		
		// Reset state
		this.retryCount = 0;
		this.isRefreshing = false;
		this.failedQueue = [];
	}

	/**
	 * Process queued requests after refresh completion
	 */
	processQueue(error, token) {
		this.failedQueue.forEach(({ resolve, reject }) => {
			if (error) {
				reject(error);
			} else {
				resolve(token);
			}
		});
		
		this.failedQueue = [];
	}

	/**
	 * Dispatch custom events for app integration
	 */
	dispatchEvent(eventName, detail) {
		window.dispatchEvent(new CustomEvent(eventName, { detail }));
	}

	/**
	 * Get token expiration date
	 */
	getTokenExpiration(token) {
		try {
			const decoded = jwt_decode(token);
			return new Date(decoded.exp * 1000);
		} catch {
			return null;
		}
	}

	/**
	 * Destroy service - clean up everything
	 */
	destroy() {
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer);
			this.refreshTimer = null;
		}
		
		this.isRefreshing = false;
		this.failedQueue = [];
		this.retryCount = 0;
	}
}

// Create singleton instance
export const tokenRefreshService = new TokenRefreshService();